import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { criticalPath } from '@planning/domain';
import { Cronograma, type EscalaCronograma } from '@xiltrion/ui/bloques/cronograma';
import { Switch } from '@xiltrion/ui/switch';
import { Label as Etiqueta } from '@xiltrion/ui/label';
import { ToggleGroup, ToggleGroupItem } from '@xiltrion/ui/toggle-group';
import { apiGet } from '@/api/client';
import { useUpdateTask } from '@/api/hooks';
import type { ViewProps } from '@/app/extension-points';
import { applyFilters } from '../tasks/view-logic';

const DAY = 86_400_000;

/** Vista Cronograma (Gantt): barras por fechas, dependencias «bloquea a», ruta crítica (calculada en el dominio) y línea base. */
export function TimelineView({ project, tasks, view, onOpenTask }: ViewProps) {
  const [scale, setScale] = useState<EscalaCronograma>((view?.config.timeline?.scale as EscalaCronograma) ?? 'week');
  const [showCritical, setShowCritical] = useState(view?.config.timeline?.showCriticalPath ?? true);
  const [showBaseline, setShowBaseline] = useState(view?.config.timeline?.showBaseline ?? false);
  const { data: cal } = useQuery({ queryKey: ['calendar'], queryFn: () => apiGet<{ holidays: string[] }>('/admin/calendar'), staleTime: 3600_000 });
  const update = useUpdateTask();
  const visible = applyFilters(tasks, view?.config).filter((t) => t.dueDate || t.startDate);
  const critical = useMemo(() => {
    if (!showCritical) return new Set<string>();
    try {
      const ids = new Set(visible.map((t) => t.id));
      const r = criticalPath(visible.map((t) => ({ id: t.id, durationDays: t.milestone ? 0 : Math.max(1, Math.round((Date.parse(t.dueDate ?? t.startDate!) - Date.parse(t.startDate ?? t.dueDate!)) / DAY) + 1), dependsOn: t.dependsOn.filter((d) => ids.has(d)) })));
      return new Set(r.criticalPath);
    } catch {
      return new Set<string>();
    }
  }, [visible, showCritical]);
  const canEdit = project ? project.capabilities.editTasks : true;
  return (
    <div className="space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-4">
        <ToggleGroup type="single" size="sm" variant="outline" value={scale} onValueChange={(v) => v && setScale(v as EscalaCronograma)}>
          <ToggleGroupItem value="day">Día</ToggleGroupItem>
          <ToggleGroupItem value="week">Semana</ToggleGroupItem>
          <ToggleGroupItem value="month">Mes</ToggleGroupItem>
          <ToggleGroupItem value="quarter">Trimestre</ToggleGroupItem>
        </ToggleGroup>
        <div className="flex items-center gap-2"><Switch id="crit" checked={showCritical} onCheckedChange={setShowCritical} /><Etiqueta htmlFor="crit">Ruta crítica</Etiqueta></div>
        <div className="flex items-center gap-2"><Switch id="base" checked={showBaseline} onCheckedChange={setShowBaseline} /><Etiqueta htmlFor="base">Línea base</Etiqueta></div>
        <span className="text-xs text-muted-foreground">{visible.length} de {tasks.length} tareas tienen fechas.</span>
      </div>
      <Cronograma
        barras={visible.map((t) => ({ id: t.id, titulo: t.title, inicio: t.startDate, fin: t.dueDate, hito: t.milestone, completada: !!t.completedAt, critica: critical.has(t.id), avance: t.completedAt ? 1 : t.checklistTotal ? t.checklistDone / t.checklistTotal : 0, lineaBaseInicio: t.baselineStart, lineaBaseFin: t.baselineDue, nivel: t.parentId ? 1 : 0 }))}
        dependencias={visible.flatMap((t) => t.dependsOn.map((d) => ({ desde: d, hasta: t.id })))}
        escala={scale}
        festivos={cal?.holidays}
        mostrarLineaBase={showBaseline}
        onAbrir={onOpenTask}
        onCambiarFechas={canEdit ? (id, inicio, fin) => update.mutate({ id, patch: { startDate: inicio, dueDate: fin } }) : undefined}
        alto={Math.max(320, Math.min(720, 52 + visible.length * 34))}
      />
    </div>
  );
}
